import React, { useCallback, useMemo, useState } from 'react';

import {
	EvChipsShowMoreProps,
	IEvFiltersPanelDefinition,
	TEvFiltersPanelValues
} from '@evinced-private/ui-common';

import { FiltersPanelOverflowModal } from 'src/common/components/filters-panel-overflow-modal';
import { FILTERS_PANEL_OVERFLOW_MODAL_LABELS } from 'src/common/components/filters-panel-overflow-modal/FiltersPanelOverflowModal.labels';
import {
	generateAppliedFilterChips,
	resetPanelFilterToDefault
} from 'src/common/helpers/FiltersPanelHelper';

interface IUseFiltersPanelOverflowModalParams {
	panelDefinitions: IEvFiltersPanelDefinition[];
	appliedValues: TEvFiltersPanelValues;
	onApply: (values: TEvFiltersPanelValues) => void;
}

interface IUseFiltersPanelOverflowModalResult {
	showMoreChipsProps: EvChipsShowMoreProps;
	overflowModal: React.ReactNode;
}

export const useFiltersPanelOverflowModal = ({
	panelDefinitions,
	appliedValues,
	onApply
}: IUseFiltersPanelOverflowModalParams): IUseFiltersPanelOverflowModalResult => {
	const [isOverflowModalOpen, setIsOverflowModalOpen] = useState(false);

	const appliedFilterChips = useMemo(
		() => generateAppliedFilterChips(appliedValues, panelDefinitions),
		[appliedValues, panelDefinitions]
	);

	const handleOpenOverflowModal = useCallback(() => {
		setIsOverflowModalOpen(true);
	}, []);

	const handleCloseOverflowModal = useCallback(() => {
		setIsOverflowModalOpen(false);
	}, []);

	const handleRemoveChip = useCallback(
		(filterId: string) => {
			const nextValues = resetPanelFilterToDefault(appliedValues, filterId, panelDefinitions);
			onApply(nextValues);

			const remainingChips = generateAppliedFilterChips(nextValues, panelDefinitions);
			if (remainingChips.length === 0) {
				setIsOverflowModalOpen(false);
			}
		},
		[appliedValues, onApply, panelDefinitions]
	);

	const showMoreChipsProps = useMemo<EvChipsShowMoreProps>(
		() => ({
			onClick: handleOpenOverflowModal,
			getLabel: FILTERS_PANEL_OVERFLOW_MODAL_LABELS.SHOW_MORE
		}),
		[handleOpenOverflowModal]
	);

	const overflowModal = (
		<FiltersPanelOverflowModal
			isOpen={isOverflowModalOpen}
			chips={appliedFilterChips}
			onClose={handleCloseOverflowModal}
			onRemoveChip={handleRemoveChip}
		/>
	);

	return { showMoreChipsProps, overflowModal };
};
